import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { horizontalDirection } from '#/service/constants';

import {
  isProductPickerDivider,
  ProductPickerItem,
} from '../PlanPurchaseFlow/PickProductStep/getPickerItems';

export interface UseProductSliderProps {
  items: ProductPickerItem[];
}

export interface UseProductSliderReturn {
  sliderRef: React.RefObject<HTMLDivElement>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  totalCards: number;
  showArrows: boolean;
  hasPromotionalLabel: boolean;
  scroll: (direction: horizontalDirection) => void;
}

export const useProductSlider = ({
  items,
}: UseProductSliderProps): UseProductSliderReturn => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const hasPromotionalLabel = items.some(
    (item) => !isProductPickerDivider(item) && item.promotionalLabel
  );

  // Count actual cards (not dividers)
  const cards = useMemo(
    () => items.filter((item) => !isProductPickerDivider(item)),
    [items]
  );

  const totalCards = cards.length;
  const showArrows = totalCards > 2;

  const updateScrollButtons = useCallback(() => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = slider;

    setCanScrollLeft(scrollLeft > 50);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  // Update scroll buttons when slider is scrolled or resized
  useEffect(() => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    updateScrollButtons();

    slider.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      slider.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons, items.length]);

  const scroll = useCallback((direction: horizontalDirection) => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    const scrollAmount = slider.clientWidth * 0.8;
    const newScrollLeft =
      direction === 'left'
        ? slider.scrollLeft - scrollAmount
        : slider.scrollLeft + scrollAmount;

    slider.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  }, []);

  return {
    sliderRef,
    canScrollLeft,
    canScrollRight,
    totalCards,
    showArrows,
    hasPromotionalLabel,
    scroll,
  };
};
